import { Element } from "@nkitaa/builder-engine";
import { motion } from "framer-motion";
import {
  type RuntimePropsWithChildren,
  SECTION_REVEAL_PROPS,
  ThemeSectionContainer,
  classNames,
  getGridClasses,
  isVisible,
  parseContentAlignmentClass,
  parseHeroOppositeWidth,
  parseHeroWidth,
  type BorderSide,
} from "./shared";
import { TextChild } from "./textChild";
import { Button } from "./button";
import { type WBReveal } from "./reveal";

export function Gallery(props: RuntimePropsWithChildren) {
  if (!isVisible(props.isVisible as boolean | undefined)) return null;
  const reveal = props.reveal as WBReveal | undefined;
  const buttonAlignment =
    (props.buttonAlignment as "flex-row" | "flex-col" | undefined) ??
    "flex-row";
  const textButtonAlignment =
    (props.textButtonAlignment as "flex-row" | "flex-col" | undefined) ??
    "flex-col";
  const padding =
    (props.padding as string | undefined) ?? "p-4 wb-md:p-6 wb-lg:p-8";
  const sectionMargin = (props.sectionMargin as string | undefined) ?? "py-0";
  const distHeadlineHeader =
    (props.distHeadlineHeader as string | undefined) ?? "mb-2";
  const distHeaderText = (props.distHeaderText as string | undefined) ?? "mb-2";
  const distTextButtons =
    (props.distTextButtons as string | undefined) ?? "gap-3";
  const distButtons = (props.distButtons as string | undefined) ?? "gap-3";
  const distButtonGallery =
    (props.distButtonGallery as string | undefined) ?? "gap-3";
  const distEntries = (props.distEntries as string | undefined) ?? "gap-3";
  const layoutWidth =
    (props.layoutWidth as "100%" | "75%" | "50%" | "33%" | "25%" | undefined) ??
    "100%";
  const inversed = (props.inversed as boolean | undefined) ?? false;
  const gridColumns =
    (props.gridColumns as "2" | "3" | "4" | "5" | "6" | undefined) ?? "3";
  const alignmentClass = parseContentAlignmentClass(
    props.contentAlignment as "top" | "center" | "bottom" | undefined,
  );
  const leftWidth = parseHeroWidth(layoutWidth);
  const rightWidth = parseHeroOppositeWidth(layoutWidth);
  return (
    <ThemeSectionContainer
      sectionHeight={props.sectionHeight as number | undefined}
      contentAlignment={
        props.contentAlignment as "top" | "center" | "bottom" | undefined
      }
      backgroundColor={props.backgroundColor as string | undefined}
      padding={padding}
      sectionMargin={sectionMargin}
      border={props.border as boolean | undefined}
      borderColor={props.borderColor as string | undefined}
      borderWidth={props.borderWidth as string | undefined}
      borderStyle={props.borderStyle as string | undefined}
      borderSides={props.borderSides as BorderSide[] | undefined}
      borderRadius={props.borderRadius as string | undefined}
      reveal={reveal}
    >
      <motion.div
        {...SECTION_REVEAL_PROPS}
        className={classNames(
          "items-center max-w-275 flex flex-col",
          alignmentClass,
          (props.isVisibleHeadline as boolean | undefined) ||
            (props.isVisibleHeader as boolean | undefined) ||
            (props.isVisibleText as boolean | undefined)
            ? "gap-5"
            : undefined,
        )}
      >
        <div
          className={classNames(
            "flex",
            distButtonGallery,
            layoutWidth !== "100%"
              ? inversed
                ? "flex-col wb-md:flex-row-reverse gap-4"
                : "flex-col wb-md:flex-row gap-4"
              : inversed
                ? "flex-col-reverse"
                : "flex-col",
          )}
        >
          <div
            className={classNames(
              leftWidth,
              "flex justify-center items-center",
              textButtonAlignment,
              distTextButtons,
            )}
          >
            <div className="w-full">
              <div className={distHeadlineHeader}>
                <Element
                  is={TextChild}
                  id="headline"
                  isVisible={props.isVisibleHeadline as boolean | undefined}
                  text={props.textHeadline as string | undefined}
                  fontSize={props.fontSizeHeadline as string | undefined}
                  textAlign={
                    props.textAlignHeadline as
                      | "left"
                      | "center"
                      | "right"
                      | "justify"
                      | undefined
                  }
                  header
                  headingLevel="h2"
                />
              </div>
              <div className={distHeaderText}>
                <Element
                  is={TextChild}
                  id="header"
                  isVisible={props.isVisibleHeader as boolean | undefined}
                  text={props.textHeader as string | undefined}
                  fontSize={props.fontSizeHeader as string | undefined}
                  textAlign={
                    props.textAlignHeader as
                      | "left"
                      | "center"
                      | "right"
                      | "justify"
                      | undefined
                  }
                  header
                  headingLevel="h1"
                />
              </div>
              <Element
                is={TextChild}
                id="text"
                isVisible={props.isVisibleText as boolean | undefined}
                text={props.textText as string | undefined}
                fontSize={props.fontSizeText as string | undefined}
                textAlign={
                  props.textAlignText as
                    | "left"
                    | "center"
                    | "right"
                    | "justify"
                    | undefined
                }
              />
            </div>
            <div
              className={classNames(
                "flex justify-center items-center",
                distButtons,
                buttonAlignment,
              )}
            >
              <Element is={Button} id="secondary" />
              <Element is={Button} id="primary" />
            </div>
          </div>
          <div
            className={classNames(
              getGridClasses(gridColumns, layoutWidth),
              distEntries,
              rightWidth,
            )}
          >
            {props.children}
          </div>
        </div>
      </motion.div>
    </ThemeSectionContainer>
  );
}
